import React, { useContext, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { Cluster, Vector } from 'ol/source';
import { GeoJSON } from 'ol/format';
import AnimatedCluster from 'ol-ext/layer/AnimatedCluster';
import { clusterClickHandler, featureStyle, getStyle } from './constants';
import SelectCluster from 'ol-ext/interaction/SelectCluster';
import VectorLayer from 'ol/layer/Vector';
import Hover from 'ol-ext/interaction/Hover';
import convexHull from 'ol-ext/geom/ConvexHull';
import { Feature } from 'ol';
import { Polygon } from 'ol/geom';
import MapContext from '../../MapContext';
import { makeFeature, scale_1_50_vector_clusterZoom } from '../../constants';

export const LayerClusters: React.FC = () => {
  const { map } = useContext(MapContext);
  const { foundPoints, allFoundPoints, showPoints } = useAppSelector(
    (state) => state.vectorLayers
  );
  const { currentLayerId } = useAppSelector((state) => state.layers);
  const { isLogged } = useAppSelector((state) => state.mainPage);
  const dispatch = useAppDispatch();
  const cluster = allFoundPoints;
  const point_radius = cluster?.point_radius || 17;
  const distance = cluster?.distance || 50;
  const distance_min = cluster?.distance_min || 20;

  useEffect(() => {
    if (!foundPoints || !foundPoints.length || !map) return;
    const styleCache = {};
    const layersData: any[] = [];

    const features = foundPoints.map((pointsArr: any, index) => {
      return pointsArr.map((item) => {
        const feature = makeFeature(item.wkt);
        feature.set('gid', item.gid);
        feature.set('wkt', item.wkt);
        feature.set('oked', item.oked);
        feature.set('index', index);

        return feature;
      });
    });

    features.forEach((feature) => {
      const vectorSource = new Vector({
        features: feature,
        format: new GeoJSON(),
      });

      const clusterSource = new Cluster({
        distance: distance,
        minDistance: distance_min,
        source: vectorSource,
      });

      let vectorLayer = new AnimatedCluster({
        source: clusterSource,
        animationDuration: 150,
        style: getStyle(styleCache, allFoundPoints.oked_colors),
        minZoom: scale_1_50_vector_clusterZoom,
      });

      map.addLayer(vectorLayer);

      // Select interaction to spread cluster out and select features
      let selectCluster = new SelectCluster({
        pointRadius: point_radius,
        animate: true,
        spiral: true,
        circleMaxObject: 300,
        maxObjects: 300,
        // Feature style when it springs apart
        featureStyle: featureStyle(feature, allFoundPoints.oked_colors),
        selectCluster: false,
        style: featureStyle(feature, allFoundPoints.oked_colors),
      });

      const vector = new VectorLayer({
        source: new Vector(),
      });
      vector.setMap(map);

      // @ts-ignore
      const hover = new Hover({
        layerFilter: function (l: any) {
          return l === vectorLayer;
        },
        cursor: 'pointer',
      });
      map.addInteraction(hover);

      hover.on('enter', function (e: any) {
        let h = e.feature.get('convexHull');
        if (!h) {
          const cluster = e.feature.get('features');
          // calculate convex hull
          if (cluster && cluster.length) {
            const c = [];
            for (let i = 0, f; (f = cluster[i]); i++) {
              // @ts-ignore
              c.push(f.getGeometry().getCoordinates());
            }
            h = convexHull(c);
            e.feature.get('convexHull', h);
          }
        }
        // @ts-ignore
        vector.getSource().clear();
        if (h.length > 2) {
          // @ts-ignore
          vector.getSource().addFeature(new Feature(new Polygon([h])));
        }
      });
      hover.on('leave', function (e) {
        vector.getSource()?.clear();
      });
      vector.setZIndex(99);
      vectorLayer.setZIndex(100);

      const layersDataRef = {
        vectorLayer,
        selectCluster,
      };
      layersData.push(layersDataRef);
    });

    const clickHandler = clusterClickHandler(
      map,
      dispatch,
      layersData,
      currentLayerId,
      isLogged
    );
    map.on('click', clickHandler);

    return () => {
      layersData.forEach((item) => {
        map.removeLayer(item.vectorLayer);
        map.removeInteraction(item.selectCluster);
      });
      map.un('click', clickHandler);
    };
  }, [map, foundPoints]);

  return null;
};
